/** Remastered boulders, diamonds and the inbox/exit door face. Pure. */

import type { RampSet, RGB } from './color';
import { Px } from './px';
import { domeNormal, hash3, lambert, normalize, step } from './shade';
import type { Vec3 } from './shade';
import { steel } from './terrain';

/** A round, knobbly rock lit from the top left, with a dark outline and a gleam. */
export function boulder(r: RampSet): Px {
  const p = new Px();
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const n = domeNormal(x, y, 8, 8.6, 7.05, 6.6);
      if (!n) continue;
      const bump = (hash3(x >> 1, y >> 1, 31) % 100) / 100 - 0.5;
      const lit = lambert(normalize([n[0] + bump * 0.3, n[1] + bump * 0.3, n[2]]));
      p.set(x, y, r.rock[step(lit, x, y, 0, 4, 0.55)] ?? r.rock[2]);
    }
  }
  p.set(5, 4, r.rock[4]);
  p.set(4, 5, r.rock[4]);
  p.set(5, 5, r.rock[4]);
  p.outline(r.ink);
  return p;
}

type Point = readonly [number, number];

interface Facet {
  readonly poly: readonly Point[];
  readonly n: Vec3;
}

/** A brilliant cut: a table, three crown facets, four pavilion facets down to the point. */
const FACETS: readonly Facet[] = [
  { poly: [[5, 2], [11, 2], [10, 4], [6, 4]], n: normalize([0, -0.3, 1]) },
  { poly: [[5, 2], [6, 4], [4, 6.5], [0.7, 6.5]], n: normalize([-0.7, -0.6, 0.5]) },
  { poly: [[6, 4], [10, 4], [12, 6.5], [4, 6.5]], n: normalize([0, -0.8, 0.6]) },
  { poly: [[11, 2], [15.3, 6.5], [12, 6.5], [10, 4]], n: normalize([0.7, -0.6, 0.5]) },
  { poly: [[0.7, 6.5], [4, 6.5], [8, 15]], n: normalize([-0.8, 0.3, 0.5]) },
  { poly: [[4, 6.5], [8, 6.5], [8, 15]], n: normalize([-0.3, 0.55, 0.8]) },
  { poly: [[8, 6.5], [12, 6.5], [8, 15]], n: normalize([0.3, 0.55, 0.8]) },
  { poly: [[12, 6.5], [15.3, 6.5], [8, 15]], n: normalize([0.8, 0.3, 0.5]) },
];

function inside(poly: readonly Point[], x: number, y: number): boolean {
  let sign = 0;
  for (let i = 0; i < poly.length; i++) {
    const [ax, ay] = poly[i] ?? [0, 0];
    const [bx, by] = poly[(i + 1) % poly.length] ?? [0, 0];
    const cross = (bx - ax) * (y - ay) - (by - ay) * (x - ax);
    if (cross !== 0) {
      if (sign === 0) sign = Math.sign(cross);
      else if (Math.sign(cross) !== sign) return false;
    }
  }
  return true;
}

const WHITE: RGB = [255, 255, 255];

/** Frame `k` of 8: the light circles round, so each facet flashes in turn; now and then a star. */
export function gem(r: RampSet, k: number): Px {
  const p = new Px();
  const a = (k * Math.PI) / 4;
  const light = normalize([-0.35 + 0.65 * Math.cos(a), -0.45 + 0.65 * Math.sin(a), 0.75]);
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      const facet = FACETS.find((f) => inside(f.poly, x + 0.5, y + 0.5));
      if (!facet) continue;
      const v = Math.pow(lambert(facet.n, light), 1.4);
      p.set(x, y, r.bright[Math.max(0, Math.min(4, Math.round(v * 4.4)))] ?? r.bright[2]);
    }
  }
  p.outline(r.ink);
  const star = k === 1 ? [4, 3] : k === 5 ? [12, 6] : null;
  if (star) {
    const [sx = 0, sy = 0] = star;
    for (const [dx, dy] of [[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]] as const) p.set(sx + dx, sy + dy, dx === 0 && dy === 0 ? WHITE : r.bright[4]);
  }
  return p;
}

/** The inbox's and the open exit's door: a steel frame round a dark doorway with light at its foot. */
export function door(r: RampSet): Px {
  const p = steel(r, 0);
  for (let y = 3; y <= 14; y++) {
    for (let x = 4; x <= 11; x++) {
      const edge = y === 3 || x === 4;
      p.set(x, y, edge ? r.ink : y >= 12 ? r.bright[y === 14 ? 3 : 1] : r.earth[0]);
    }
  }
  p.set(10, 9, r.bright[4]);
  p.set(10, 10, r.bright[2]);
  return p;
}
